import React, { useState } from "react";

import { Eye, EyeOff } from "lucide-react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInputBase,
} from "@/src/components/form-fields/InputGroup";
import { Button } from "@/src/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/src/components/ui/field";

import type { LucideIcon } from "lucide-react";
import type { JSX } from "react";

interface LoginFieldProps {
  name: string;
  label: string;
  placeholder: string;
  icon: LucideIcon;
  type?: string;
  autoComplete?: string;
  withVisibilityToggle?: boolean;
}

function LoginField({
  name,
  label,
  placeholder,
  icon: Icon,
  type = "text",
  autoComplete,
  withVisibilityToggle = false,
}: LoginFieldProps): JSX.Element {
  const { t } = useTranslation("login");
  const [isValueVisible, setIsValueVisible] = useState(false);

  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  const error = fieldState.error?.message;
  const resolvedType = withVisibilityToggle && isValueVisible ? "text" : type;

  return (
    <Field>
      <FieldLabel className="sr-only" htmlFor={name}>
        {label}
      </FieldLabel>
      <InputGroup invalid={Boolean(error)}>
        <InputGroupAddon className="px-3">
          <Icon className="text-muted-foreground size-4" />
        </InputGroupAddon>
        <InputGroupInputBase
          autoComplete={autoComplete}
          id={name}
          onBlur={field.onBlur}
          onChange={(event) => field.onChange(event.target.value)}
          placeholder={placeholder}
          type={resolvedType}
          value={field.value ?? ""}
        />
        {withVisibilityToggle ? (
          <InputGroupAddon className="border-r-0 bg-transparent pr-1">
            <Button
              className="text-muted-foreground"
              onClick={() => setIsValueVisible((isVisible) => !isVisible)}
              size="icon-sm"
              type="button"
              variant="ghost"
              aria-label={
                isValueVisible ? t("hidePassword") : t("showPassword")
              }
            >
              {isValueVisible ? <EyeOff /> : <Eye />}
            </Button>
          </InputGroupAddon>
        ) : null}
      </InputGroup>
      <FieldError>{error}</FieldError>
    </Field>
  );
}

export default LoginField;
